import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  SxProps,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { FormikProps } from 'formik';
import { useMemo, useState, useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { KpiFormValues, WorkpackLogs, WorklogsField, WorklogsSection } from '../../types';
import {
  convertValueByUnit,
  getLoggedValueForField,
  getSectionUnitLabel,
  getSectionUnitSymbol,
} from '../../utils';
import * as globalState from '@/components/Analysis/state';
import {
  accordionStyles,
  getAccordionSummaryStyles,
  headerCellStyles,
  bodyCellStyles,
  inputCellStyles,
  textFieldStyles,
  tooltipStyles,
  accordianDetailsStyles,
} from './styles';
import { TotalSumRow } from '../KpiTotalSumRow';
import { DiscrepancyReasonField } from '../DiscrepancyReasonField';
import { SectionUnitsEnum } from '@/__generated__/graphql';
import { KPI_NUMBER_OF_DAILY_HOURS } from '../../constants';
import { KpiExpectedTotalHours } from './KpiExpectedTotalHours';
import { Banner } from '@/components/shared/Banner';
import { KpiCommentTableCell } from './KpiCommentTableCell';

type Props = {
  section: WorklogsSection;
  fields: WorklogsField[];
  workpackLogs: WorkpackLogs | undefined;
  logsLoading: boolean;
  formik: FormikProps<KpiFormValues>;
  isSubmitting: boolean;
};

export const KpiPanelSection = ({
  section,
  fields,
  workpackLogs,
  logsLoading,
  formik,
  isSubmitting,
}: Props) => {
  const [expanded, setExpanded] = useState(section.defaultOpen ?? true);
  const pob = useMemo(() => {
    const val = formik.values.pob?.value;
    const numericVal = Number(val);
    return isNaN(numericVal) ? 0 : numericVal;
  }, [formik.values?.pob?.value]);

  const sectionFields = useMemo(
    () =>
      section.fieldKeys
        .map((fieldKey) => fields.find((field) => field.key === fieldKey))
        .filter((field) => section.fieldKeys.includes(field?.key || '')),
    [fields, section.fieldKeys]
  );

  const unitSystem = useAtomValue(globalState.unitSystem);
  const unitLabel = getSectionUnitLabel(section.sectionUnit);
  const unitSymbol = getSectionUnitSymbol(section.sectionUnit, unitSystem);

  const getDisplayValue = useCallback(
    (value: number): number => {
      return convertValueByUnit(value, section.sectionUnit, unitSystem, 'view');
    },
    [section.sectionUnit, unitSystem]
  );

  const handleFieldChange = (fieldKey: string, value: string) => {
    const currentField = formik.values[fieldKey];
    formik.setFieldValue(fieldKey, {
      ...currentField,
      value: value === '' ? '' : Number(value),
    });
  };

  // Calculate totals
  const totalLogged = useMemo(() => {
    return sectionFields.reduce((sum, field) => {
      if (field) {
        const loggedValue = getLoggedValueForField(field.key, workpackLogs);
        return sum + getDisplayValue(loggedValue);
      }
      return sum;
    }, 0);
  }, [sectionFields, workpackLogs, getDisplayValue]);

  const totalFormValue = useMemo(() => {
    return sectionFields.reduce((sum, field) => {
      if (field && field.key) {
        const fieldKey = field.key;
        const formValue = formik.values[fieldKey]?.value;
        return sum + (typeof formValue === 'number' ? formValue : 0);
      }
      return sum;
    }, 0);
  }, [sectionFields, formik.values]);

  const isHoursSection = section.sectionUnit === SectionUnitsEnum.Hours;

  const discrepancyInfo = useMemo(() => {
    if (!isHoursSection) return;

    const maximumHours = KPI_NUMBER_OF_DAILY_HOURS * pob;
    if (maximumHours === 0 || typeof maximumHours !== 'number') return;
    if (totalFormValue === maximumHours) return;

    const difference = Math.abs(totalFormValue - maximumHours);
    const isOver = totalFormValue > maximumHours;

    return {
      shouldShow: true,
      difference,
      isOver,
      message: `Logged hours are ${difference} hour${difference !== 1 ? 's' : ''} ${
        isOver ? 'higher' : 'lower'
      } than expected, please provide a reason`,
    };
  }, [isHoursSection, pob, totalFormValue]);

  const disableAllFields = (() => {
    // Only need this on 'Hours' sections
    if (!isHoursSection) return false;
    // User must enter a POB before entering any hours
    if (!pob) return true;
    // This shouldn't happen but just in case...
    if (pob < 0) return true;
    return false;
  })();

  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        disableGutters
        sx={accordionStyles}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={getAccordionSummaryStyles(expanded)}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.6rem' }}
            >
              {section.title}
            </Typography>
            {isHoursSection && pob > 0 && <KpiExpectedTotalHours pob={pob} />}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={accordianDetailsStyles}>
          {sectionFields.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              No fields found for this section
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headerCellStyles, width: '32%' }}>Category</TableCell>
                    <TableCell sx={{ ...headerCellStyles, width: '28%' }}>
                      Total Logged {unitLabel ?? ''}
                      {unitSymbol ? ` (${unitSymbol})` : ''} Till Selected Date
                    </TableCell>
                    <TableCell sx={{ ...headerCellStyles, width: '30%' }}>
                      {unitLabel ?? ''}
                      {unitSymbol ? ` (${unitSymbol})` : ''} Logged On Selected Date
                    </TableCell>
                    <TableCell sx={{ ...headerCellStyles, width: '10%', textAlign: 'center' }}>
                      Comment
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {sectionFields.map((field) => {
                    const fieldKey = field?.key || '';
                    const touched = formik.touched[fieldKey];
                    const fieldError = formik.errors[fieldKey];
                    // Access the nested value error
                    const error =
                      typeof fieldError === 'object' && fieldError && 'value' in fieldError
                        ? String(fieldError.value || '')
                        : undefined;
                    const showError = touched && !!error;

                    if (field) {
                      return (
                        <FieldRow
                          key={field?.id}
                          field={field}
                          section={section}
                          loggedValue={getDisplayValue(
                            getLoggedValueForField(field?.key, workpackLogs)
                          )}
                          formValue={formik.values[fieldKey]?.value ?? ''}
                          isLoading={logsLoading}
                          onChange={handleFieldChange}
                          onBlur={formik.handleBlur}
                          disabled={isSubmitting || disableAllFields}
                          error={showError ? error : undefined}
                        />
                      );
                    }

                    return <></>;
                  })}

                  {/* Only need to show total sum row for hours section */}
                  {isHoursSection && (
                    <TotalSumRow
                      unitLabel={unitLabel}
                      loading={logsLoading}
                      totalLogged={totalLogged}
                      totalFormValue={totalFormValue}
                      isError={discrepancyInfo?.shouldShow}
                    />
                  )}
                </TableBody>
              </Table>

              {/* Show a warning banner if POB is not set with an hours section */}
              {isHoursSection && !pob && (
                <Box sx={{ mt: 1 }}>
                  <Banner
                    message={'Set POB from the top to calculate total hours.'}
                    severity="warning"
                  />
                </Box>
              )}

              <DiscrepancyReasonField
                sectionKey={section.key}
                sx={{ mt: 1 }}
                helperText={discrepancyInfo?.message}
                isError={!!discrepancyInfo?.shouldShow}
                isPobEmpty={!pob}
              />
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

type FieldRowProps = {
  field: WorklogsField;
  section: WorklogsSection;
  loggedValue: number;
  formValue: number | '';
  isLoading: boolean;
  onChange: (fieldKey: string, value: string) => void;
  onBlur: (e: React.FocusEvent) => void;
  disabled?: boolean;
  error?: string;
};

const FieldRow = ({
  field,
  section,
  loggedValue,
  formValue,
  isLoading,
  onChange,
  onBlur,
  disabled,
  error,
}: FieldRowProps) => {
  return (
    <TableRow sx={{ border: 1, borderColor: 'divider' }}>
      <TableCell
        sx={{
          ...bodyCellStyles,
          color: 'text.primary',
          border: 1,
          borderRight: 0,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {field.name}
          {field.fieldDescription && (
            <Tooltip title={field.fieldDescription} arrow placement="top" slotProps={tooltipStyles}>
              <InfoIcon fontSize="small" sx={{ cursor: 'pointer' }} />
            </Tooltip>
          )}
        </Box>
      </TableCell>

      <TableCell
        sx={{
          ...bodyCellStyles,
          color: 'text.primary',
          border: 1,
          borderColor: 'divider',
          borderRight: '1px solid #7E9BBC !important',
          borderLeft: '1px solid #E0E0E0 !important',
        }}
      >
        {isLoading ? <Skeleton variant="text" /> : loggedValue}
      </TableCell>

      <TableCell sx={{ ...bodyCellStyles, ...inputCellStyles } as SxProps}>
        <TextField
          size="small"
          type="number"
          variant="outlined"
          placeholder="0"
          fullWidth
          name={field.key || ''}
          id={field.key || ''}
          value={formValue}
          onChange={(e) => field.key && onChange(field.key, e.target.value)}
          onBlur={onBlur}
          slotProps={{
            htmlInput: { min: 0, 'aria-describedby': error ? `${field.key}-error` : undefined },
          }}
          sx={textFieldStyles}
          disabled={disabled}
          error={!!error}
          helperText={error}
          aria-invalid={!!error}
        />
      </TableCell>
      <KpiCommentTableCell field={field} section={section} />
    </TableRow>
  );
};
